import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Edit2, Trash2, Upload, Loader2 } from 'lucide-react';

const EMPTY = { name: '', role: '', bio: '', photo_url: '', order: 0 };

export const AdminTeamPage = () => {
  const { api, token } = useAuth();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const photoInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api().get('/team');
      setMembers(res.data);
    } catch { toast.error('Failed to load team'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const startNew = () => { setEditing(null); setForm({ ...EMPTY, order: members.length }); setOpen(true); };
  const startEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name || '', role: m.role || '',
      bio: m.bio || '', photo_url: m.photo_url || '',
      order: m.order ?? 0
    });
    setOpen(true);
  };

  const handlePhotoUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch(`${API}/upload?folder=team`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      if (!res.ok) throw new Error('Upload failed');
      const data = await res.json();
      const url = `${API.replace(/\/api$/, '')}/api/files/${data.path}`;
      setForm(prev => ({ ...prev, photo_url: url }));
      toast.success('Photo uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.name || !form.role) { toast.error('Name and role required'); return; }
    try {
      if (editing) await api().put(`/team/${editing.id}`, form);
      else await api().post('/team', form);
      toast.success(editing ? 'Member updated' : 'Member added');
      setOpen(false);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Save failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this team member?')) return;
    try {
      await api().delete(`/team/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Delete failed'); }
  };

  return (
    <div data-testid="admin-team-page" className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold uppercase tracking-wide">Team</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage team bios and photos shown on the public "Meet The Team" page.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-btn" onClick={startNew} className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              <Plus className="w-4 h-4 mr-2" /> Add Team Member
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading uppercase">{editing ? 'Edit Member' : 'New Member'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSave} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Name *</Label>
                <Input data-testid="team-name-input" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Role / Title *</Label>
                <Input data-testid="team-role-input" required value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} placeholder="e.g. Founder, Senior Official, Director" className="bg-zinc-800 border-zinc-700" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Bio</Label>
                <Textarea data-testid="team-bio-input" value={form.bio} onChange={e => setForm({ ...form, bio: e.target.value })} rows={6} className="bg-zinc-800 border-zinc-700" placeholder="Background, years officiating, notable games..." />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Photo</Label>
                <div className="flex flex-col sm:flex-row gap-2 mt-1">
                  <input ref={photoInputRef} type="file" accept="image/*" className="hidden" onChange={e => handlePhotoUpload(e.target.files?.[0])} data-testid="team-photo-input" />
                  <Button type="button" onClick={() => photoInputRef.current?.click()} disabled={uploading} data-testid="team-photo-upload-btn" className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700 flex-shrink-0">
                    {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                    Upload from Device
                  </Button>
                  <Input value={form.photo_url} onChange={e => setForm({ ...form, photo_url: e.target.value })} placeholder="or paste URL" className="bg-zinc-800 border-zinc-700" />
                </div>
                {form.photo_url && <img src={form.photo_url} alt="" className="mt-3 w-28 h-36 object-cover rounded border border-zinc-800" />}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Display Order</Label>
                <Input type="number" value={form.order} onChange={e => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-zinc-800 border-zinc-700" />
              </div>
              <Button type="submit" data-testid="save-team-btn" className="w-full bg-amber-500 hover:bg-amber-400 text-black font-bold">
                {editing ? 'Update' : 'Add Member'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-2 border-white border-t-transparent rounded-full" /></div>
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 bg-zinc-900/50 border border-zinc-800 rounded text-center">
          <Users className="w-10 h-10 text-zinc-600 mb-3" />
          <p className="text-zinc-500">No team members yet.</p>
          <Button className="mt-4 bg-amber-500 hover:bg-amber-400 text-black font-bold" onClick={startNew}><Plus className="w-4 h-4 mr-2" /> Add First Member</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {members.map((m, idx) => (
            <div key={m.id} data-testid={`admin-team-${idx}`} className="bg-zinc-900/50 border border-zinc-800 rounded-lg overflow-hidden">
              <div className="aspect-[3/4] bg-zinc-950 relative">
                {m.photo_url ? <img src={m.photo_url} alt={m.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><Users className="w-12 h-12 text-zinc-700" /></div>}
              </div>
              <div className="p-4">
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-amber-500">{m.role}</p>
                <h3 className="font-heading font-bold text-lg uppercase mt-1">{m.name}</h3>
                {m.bio && <p className="text-xs text-zinc-500 mt-2 line-clamp-2">{m.bio}</p>}
                <div className="flex gap-2 mt-4">
                  <Button size="sm" variant="outline" onClick={() => startEdit(m)} data-testid={`edit-team-${idx}`} className="flex-1 border-zinc-700"><Edit2 className="w-3.5 h-3.5 mr-1.5" /> Edit</Button>
                  <Button size="sm" variant="outline" onClick={() => handleDelete(m.id)} data-testid={`delete-team-${idx}`} className="border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="w-3.5 h-3.5" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
